import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  CalendarDays,
  CheckCircle2,
} from "lucide-react";
import { Link, useParams } from "react-router";
import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";

type KnowledgeArticle = {
  id: string;
  title: string;
  summary: string;
  updatedAt: string;
  readTime: string;
  metrics?: {
    label: string;
    value: string;
    hint: string;
  }[];
  sections: {
    title: string;
    body: string;
    points: string[];
    tables?: KnowledgeTable[];
    charts?: KnowledgeBarChart[];
  }[];
};

type KnowledgeTable = {
  title: string;
  caption?: string;
  headers: string[];
  rows: string[][];
};

type KnowledgeBarChart = {
  title: string;
  caption?: string;
  unit?: string;
  items: {
    label: string;
    value: number;
    valueLabel?: string;
  }[];
};

const articles: KnowledgeArticle[] = [
  {
    id: "doka-system-description",
    title: "Описание системы Doka",
    summary:
      "Системное описание личного кабинета для аудита: назначение, роли, рабочие контуры, доменная модель и техническая архитектура.",
    updatedAt: "Июнь 2026",
    readTime: "8 мин",
    sections: [
      {
        title: "Назначение",
        body: "Doka — веб-система для проведения аудиторских проектов, сбора документов от аудируемых организаций и визуальной работы с полученными материалами.",
        points: [
          "Аудиторы создают проекты, формируют требования к клиенту, назначают команду и контролируют прогресс сбора документов.",
          "Аудируемые лица работают в личном кабинете: видят доступные проекты, отвечают на запросы и загружают файлы в конкретные слоты требований.",
          "Полученные документы связываются с проектом, требованием и слотом, хранятся в объектном хранилище и могут раскладываться на рабочей доске проекта.",
        ],
      },
      {
        title: "Пользователи и доступ",
        body: "Система разделяет глобальные роли и проектные права, чтобы аудиторы и представители клиента видели только допустимые проекты, файлы и действия.",
        points: [
          "Аудитор-администратор управляет проектами, аудиторами, пользователями, настройками организации, форматами файлов, SMTP и моделью статусов.",
          "Аудитор работает с назначенными проектами, требованиями, файлами и доской в пределах выданных прав.",
          "Аудируемое лицо видит свои проекты, загружает и скачивает файлы, отвечает на текстовые запросы, а при роли team admin управляет участниками клиентской команды.",
        ],
      },
      {
        title: "Основные контуры системы",
        body: "Центром работы является аудиторский проект: вокруг него собираются клиент, сроки, договор, команда, требования, документы, статусы и рабочая доска.",
        points: [
          "Проекты хранят данные клиента, ИНН, договор, период проверки, существенность, дедлайн, статус, аудиторов проекта и участников клиента.",
          "Требования поддерживают три формата: текстовый запрос с ответом, одиночные файловые слоты и табличные сетки слотов.",
          "Доска проекта на React Flow позволяет раскладывать материалы в файловые карточки, файловые сетки и табличные структуры.",
          "Справочник клиентов хранит реквизиты аудируемых организаций, контакты, сотрудников и предложения изменений от стороны клиента.",
        ],
      },
      {
        title: "Рабочие сценарии",
        body: "Типовой цикл начинается с создания проекта и заканчивается структурированием полученных документов на доске аудитора.",
        points: [
          "Аудитор-администратор создает проект, заполняет параметры проверки, назначает аудиторов и участников клиента.",
          "Аудитор формирует требования, указывает тип запроса, допустимые форматы, лимиты файлов, подписи слотов и отправляет запрос клиенту.",
          "Аудируемое лицо принимает приглашение, открывает требования проекта и загружает документы через resumable upload.",
          "Система пересчитывает прогресс требований, фиксирует события активности и делает материалы доступными аудитору.",
          "Аудитор анализирует полученные файлы, переносит их на доску, группирует по процедурам и при необходимости работает совместно с командой.",
        ],
      },
      {
        title: "Данные и доменная модель",
        body: "Доменная модель построена вокруг Payload CMS: коллекции описывают пользователей, проекты, клиентов, файлы, приглашения и события доступа.",
        points: [
          "Ключевые сущности: users, projects, client-organizations, media, slot-uploads, project-member-invites, staff-invites и журналы событий.",
          "В проекте хранятся требования, snapshot доски, агрегаты прогресса документов, настройки файловых прав и ограничения форматов.",
          "Файлы представлены двумя уровнями: бинарное содержимое в S3/MinIO и метаданные Media/slot-uploads в Payload/MongoDB.",
          "Глобальные настройки app-settings управляют данными организации, допустимыми форматами, логотипом, favicon и SMTP-метаданными.",
        ],
      },
      {
        title: "Архитектура",
        body: "Doka состоит из Next.js-приложения, Payload CMS, MongoDB, объектного хранилища, tusd для устойчивой загрузки файлов и опционального сервера совместной работы.",
        points: [
          "Next.js App Router отвечает за фронтенд личного кабинета, API routes, серверные страницы и server actions.",
          "Payload CMS используется как слой доменной модели, access control, Local API и низкоуровневая административная панель.",
          "MongoDB хранит документы Payload: пользователей, проекты, требования, события, настройки и связи.",
          "S3/MinIO хранит бинарные файлы, а tusd обеспечивает возобновляемую загрузку больших документов из браузера.",
          "React Flow реализует рабочую доску проекта, Hocuspocus/Yjs включают совместное редактирование доски в реальном времени.",
        ],
      },
    ],
  },
  {
    id: "doka-product-strategy",
    title: "Стратегия развития продукта Doka",
    summary:
      "Фокус продукта, целевые пользователи, ключевые направления развития и метрики, по которым стоит оценивать прогресс.",
    updatedAt: "Июнь 2026",
    readTime: "6 мин",
    sections: [
      {
        title: "Цель",
        body: "Doka должна стать рабочим центром для аудиторских компаний: от первичной базы клиентов до контроля сделок, задач и управленческой аналитики.",
        points: [
          "Сократить ручную работу при ведении клиентской базы.",
          "Дать руководителю прозрачность по воронке и активности команды.",
          "Собрать в одном интерфейсе контакты, компании, задачи, заметки и историю взаимодействий.",
        ],
      },
      {
        title: "Фокус развития",
        body: "Приоритетом остаются функции, которые помогают быстро запустить CRM на реальных данных и ежедневно поддерживать порядок в клиентской базе.",
        points: [
          "Импорт и обогащение компаний из открытых источников.",
          "Удобная сегментация по региону, ответственному менеджеру и размеру компании.",
          "База знаний для методологии продаж, материалов по продукту и внутренних регламентов.",
        ],
      },
      {
        title: "Метрики",
        body: "Продуктовая стратегия должна проверяться измеримыми показателями использования, а не только объемом добавленных функций.",
        points: [
          "Доля компаний с заполненными контактами и ответственным менеджером.",
          "Количество активных задач и заметок на одного пользователя.",
          "Скорость перехода от импортированной компании к первому контакту или сделке.",
        ],
      },
    ],
  },
  {
    id: "business-plan",
    title: "Бизнес план Doka",
    summary:
      "Конспект бизнес-плана из документа: продукт, рынок, модули, продажи, расходы, финансовая модель и основные риски проекта.",
    updatedAt: "Июнь 2026",
    readTime: "10 мин",
    metrics: [
      {
        label: "Выручка за год",
        value: "28 380",
        hint: "тыс. руб. по двум модулям",
      },
      {
        label: "Расходы",
        value: "25 055",
        hint: "тыс. руб. за 12 месяцев",
      },
      {
        label: "Чистая прибыль",
        value: "2 327",
        hint: "после УСН",
      },
      {
        label: "Целевая база",
        value: "2–30",
        hint: "аудиторов в МСБ-сегменте",
      },
    ],
    sections: [
      {
        title: "Резюме",
        body: "Проект направлен на разработку и вывод на рынок облачного программного продукта «Личный кабинет аудируемого лица» — B2B-платформы для защищенного цифрового взаимодействия между аудиторскими организациями и их клиентами.",
        points: [
          "Ценность продукта — интеграция взаимодействия с клиентом в процесс документирования аудита.",
          "Продукт заменяет электронную почту, мессенджеры и Excel-таблицы единым защищенным пространством.",
          "Целевой рынок — аудиторские организации России, в перспективе СНГ, Европа и арабские страны.",
          "Горизонт планирования в документе — 1 год, август 2026 — июль 2027.",
          "Целевая аудитория — аудиторские фирмы МСБ с 2–30 аудиторами.",
        ],
        tables: [
          {
            title: "Параметры бизнес-плана",
            caption:
              "Из таблицы резюме документа. Пустые значения оставлены как «требует уточнения».",
            headers: ["Параметр", "Значение"],
            rows: [
              ["Название продукта", "Личный кабинет аудируемого лица (ЛКАЛ)"],
              [
                "Модель монетизации",
                "SaaS для личного кабинета, локальное ПО для документирования аудита",
              ],
              [
                "Целевой рынок",
                "Аудиторские организации России; перспектива: СНГ, Европа, арабские страны",
              ],
              ["Горизонт планирования", "1 год (август 2026 — июль 2027)"],
              ["Объем инвестиций", "Требует уточнения"],
              ["Окупаемость", "Требует уточнения"],
              ["Целевая аудитория", "Аудиторские фирмы МСБ, 2–30 аудиторов"],
              ["Прогноз выручки", "Требует уточнения"],
            ],
          },
        ],
      },
      {
        title: "Проблема рынка",
        body: "Аудиторские фирмы сталкиваются с неэффективностью взаимодействия с аудируемыми лицами: документы, запросы, ответы, версии и статусы часто живут в разных каналах.",
        points: [
          "Запросы документов ведутся через почту, письма теряются, версии документов путаются.",
          "Нет единого реестра запросов: аудитор не знает, что уже получено, а клиент не понимает, что еще нужно предоставить.",
          "Конфиденциальные финансовые документы передаются по незащищенным каналам.",
          "Согласование замечаний и ответов происходит хаотично.",
          "Нет журнала действий для восстановления истории при спорных ситуациях.",
          "Интеграция с существующими программами аудита, включая IT Audit и AuditXP, отсутствует.",
        ],
      },
      {
        title: "Продукт",
        body: "В документе рассматриваются рабочие названия «АудитВоркСпейс», «Дока» и «ЛКАЛ». ЛКАЛ описан как облачная SaaS-платформа с двумя интерфейсами.",
        points: [
          "Рабочее место аудитора: управление проектами, запросы, контроль исполнения и интеграция с аудиторской программой.",
          "Личный кабинет клиента: загрузка документов, ответы на запросы, переписка и просмотр статусов.",
          "Модель монетизации: SaaS для личного кабинета и локальное ПО для документирования аудита.",
          "Организационная форма в документе: ИП Бондарчук или ООО «ИстПромЭкспорт».",
        ],
      },
      {
        title: "Модули и услуги",
        body: "Продуктовая линейка разделена на обязательный модуль запросов и дополнительные модули, которые расширяют ценность для аудиторской команды.",
        points: [
          "Модули ПО: модуль запроса, модуль документирования проекта, модуль управления проектами.",
          "Обязательный модуль — модуль запроса; остальные модули позиционируются как дополнительные.",
          "В документе указаны этапы запуска: 01.07.2026, 01.10.2026 и 2027 год; последняя очередь в бизнес-плане не учитывается.",
          "Дополнительные функции: ИИ-агент для анализа полученных документов, ИИ-агент для аудиторских процедур, внутрикорпоративный чат.",
          "Сопутствующие услуги: настройка сервера, комплект оборудования «все включено», месячная или годовая техническая поддержка.",
        ],
      },
      {
        title: "SWOT",
        body: "SWOT-анализ выделяет сильную сторону продукта в визуализации аудиторского проекта и контроле запросов в одном пространстве.",
        points: [
          "Сильные стороны: визуализация проекта на одной доске, запрос и ответ в одном месте, self-hosted-развертывание на собственном или арендованном сервере.",
          "Дополнительные сильные стороны: интеграция с Excel, AuditIT и AuditXP, обработка запросов «по частям» с отметкой об использовании документа.",
          "Слабая сторона, указанная в документе: слабые связи внутри процедур, когда входящие данные в каждом рабочем документе нужно загружать заново.",
          "Возможности: управление проектами, внедрение общих справочников компании и проекта, электронная подпись документов и логирование неизменности.",
          "Угрозы: использование дашбордов конкурентами и изменение сути аудита при переходе к проверкам в режиме онлайн.",
        ],
        tables: [
          {
            title: "SWOT-анализ",
            headers: ["Контур", "Позитивные факторы", "Негативные факторы"],
            rows: [
              [
                "Текущие, внутренние",
                "Визуализация проекта на одной доске; запрос и ответ в одном месте; self-hosted-решение; интеграция с Excel, AuditIT и AuditXP; обработка запросов по частям",
                "Слабые связи внутри процедур: входящие данные в каждом рабочем документе нужно загружать заново",
              ],
              [
                "Будущие, внешние",
                "Управление проектами; общие справочники компании и проекта; электронная подпись и логирование неизменности документов",
                "Конкуренты могут использовать дашборды; аудит может перейти к онлайн-проверкам",
              ],
            ],
          },
        ],
      },
      {
        title: "Технологии и требования",
        body: "Технический контур в бизнес-плане ориентирован на импортозамещение, безопасность и развертывание в российской инфраструктуре.",
        points: [
          "Backend: Python/FastAPI или Node.js, PostgreSQL, Redis.",
          "Frontend: React.js и адаптивный дизайн для desktop и mobile.",
          "Инфраструктура: Яндекс Cloud или VK Cloud, Docker/Kubernetes.",
          "Безопасность: AES-256, TLS 1.3, двухфакторная аутентификация.",
          "Соответствие: ФЗ-152 о персональных данных и хранение данных на территории РФ.",
          "Требования по сегментам: от 200 ГБ SSD, 4 ГБ RAM и 4 CPU для микро-сегмента до 1500 ГБ SSD, 32 ГБ RAM и 16 CPU для крупных организаций.",
          "Дополнительные условия: отправлять запросы может только руководитель проекта, видеокарта не нужна, VPN рекомендуется.",
        ],
        tables: [
          {
            title: "Технические требования по сегментам",
            headers: ["Параметр", "Микро", "Малая", "Средняя", "Крупная"],
            rows: [
              ["Руководители проекта", "1–4", "5–7", "8–12", "13–30"],
              ["Среднее руководителей", "3", "6", "10", "21"],
              ["Пользователи", "1–16", "17–28", "29–48", "49–120"],
              ["Проекты", "1–80", "81–140", "141–240", "241–600"],
              ["SSD, ГБ", "200", "400", "600", "1500"],
              ["RAM, ГБ", "4", "8", "16", "32"],
              ["CPU, ядер", "4", "8", "8", "16"],
            ],
          },
        ],
      },
      {
        title: "Организационный план",
        body: "В документе зафиксированы доли учредителей, роли команды и базовая модель затрат на персонал.",
        points: [
          "Учредители: Бондарчук С.А. — 70%, Бареев — 30%.",
          "Роли: директор, разработчик и Product Owner — Бондарчук С.А.; Product Manager — Бареев Т.Ф.; системный администратор — Радик; также предусмотрен менеджер по продажам.",
          "Бондарчук С.А. — 0,5 ставки, 400 тыс. руб.",
          "Бухгалтерия на аутсорсе — 0,1 ставки, 13 тыс. руб.",
          "СисАдмин — 60% от продаж.",
          "Менеджер по продажам — 0,5 ставки, 60 тыс. руб. плюс 10% от продаж.",
        ],
      },
      {
        title: "Рынок и конкуренты",
        body: "В документе указано, что self-hosted-программы личного кабинета для взаимодействия с клиентом на рынке аудиторских услуг не представлены.",
        points: [
          "Сегменты рынка: микро, малые, средние, крупные организации и топ-30.",
          "Оценка базы: микро — 966 компаний, малые — 665, средние — 232, крупные — 116, топ-30 — 30.",
          "Средняя выручка по сегментам в таблице: микро — 5 865, малые — 9 504, средние — 18 177, крупные — 56 780, топ-30 — 1 184 591.",
          "Конкуренты: IT Audit, AuditXP Professional, электронная почта + Dropbox, 1С-Документооборот, зарубежные TeamMate и CaseWare.",
          "У IT Audit сильная методология и бренд, но нет клиентского портала.",
          "У AuditXP широкая база пользователей, но устаревший UX и нет клиентского портала.",
          "Почта/Dropbox знакомы и бесплатны, но небезопасны, хаотичны и не дают аудит-трейла.",
        ],
        charts: [
          {
            title: "Количество аудиторских компаний по сегментам",
            unit: "компаний",
            items: [
              { label: "Микро", value: 966 },
              { label: "Малая", value: 665 },
              { label: "Средняя", value: 232 },
              { label: "Крупная", value: 116 },
              { label: "Топ-30", value: 30 },
            ],
          },
          {
            title: "Средняя выручка по сегментам",
            unit: "тыс. руб.",
            items: [
              { label: "Микро", value: 5865, valueLabel: "5 865" },
              { label: "Малая", value: 9504, valueLabel: "9 504" },
              { label: "Средняя", value: 18177, valueLabel: "18 177" },
              { label: "Крупная", value: 56780, valueLabel: "56 780" },
              { label: "Топ-30", value: 1184591, valueLabel: "1 184 591" },
            ],
          },
        ],
        tables: [
          {
            title: "Анализ рынка аудита",
            headers: [
              "Категория",
              "Аудиторы, мин.",
              "Аудиторы, макс.",
              "Аудиторы, среднее",
              "Компаний",
              "Средняя выручка",
            ],
            rows: [
              ["Микро", "1", "4", "3", "966", "5 865"],
              ["Малая", "5", "7", "6", "665", "9 504"],
              ["Средняя", "8", "12", "9", "232", "18 177"],
              ["Крупная", "13", "33", "19", "116", "56 780"],
              ["Топ-30", "34", "233", "92", "30", "1 184 591"],
            ],
          },
          {
            title: "Конкурентная среда",
            headers: ["Решение", "Сильные стороны", "Слабые стороны", "Угроза"],
            rows: [
              [
                "IT Audit",
                "Глубокая методология, известный бренд",
                "Нет клиентского портала, только для аудиторов",
                "Средняя",
              ],
              [
                "AuditXP Professional",
                "Широкая база пользователей (~50% рынка)",
                "Устаревший UX, нет портала клиента",
                "Средняя",
              ],
              [
                "Электронная почта + Dropbox",
                "Знакомо, бесплатно",
                "Небезопасно, хаотично, нет аудит-трейла",
                "Низкая",
              ],
              [
                "1С-Документооборот",
                "Мощный функционал",
                "Не специализирован под аудит, дорого",
                "Низкая",
              ],
              [
                "TeamMate / CaseWare",
                "Развитые функции",
                "Нет локализации, уход с рынка РФ",
                "Отсутствует",
              ],
            ],
          },
        ],
      },
      {
        title: "Продажи и маркетинг",
        body: "План продаж строится вокруг сегментации аудиторских организаций по размеру и набора модулей с разной стоимостью.",
        points: [
          "Модуль запросов: 10 000 руб. в год за одного руководителя проекта.",
          "Модуль документирования: 20 000 руб. бессрочно за одного руководителя проекта.",
          "Модуль планирования: 50 000 руб. бессрочно за одну компанию.",
          "Стоимость обновления модулей документирования и планирования: 40 000 руб. в год.",
          "Абонемент: от 2 000 до 8 000 руб. в месяц или от 19 200 до 76 800 руб. в год в зависимости от сегмента.",
          "ИИ в запросах: 10 000 руб. в год за одного руководителя; ИИ в процедурах: 20 000 руб. в год за одного руководителя.",
          "Каналы привлечения: прямые продажи на конференциях аудиторов и форумах СРО ААС, партнерства с IT Audit и AuditXP, контент-маркетинг, реферальная программа и 30-дневный пробный период.",
        ],
        tables: [
          {
            title: "Цены по сегментам",
            headers: ["Показатель", "Микро", "Малая", "Средняя", "Крупная"],
            rows: [
              [
                "Запросы, год, 1 руководитель",
                "10 000",
                "10 000",
                "10 000",
                "10 000",
              ],
              [
                "Документирование, бессрочно",
                "20 000",
                "20 000",
                "20 000",
                "20 000",
              ],
              [
                "Планирование, за 1 компанию",
                "50 000",
                "50 000",
                "50 000",
                "50 000",
              ],
              ["Обновление, год", "40 000", "40 000", "40 000", "40 000"],
              ["Абонемент, месяц", "2 000", "4 000", "8 000", "8 000"],
              ["Абонемент, год", "19 200", "38 400", "76 800", "76 800"],
              ["ИИ в запросах, год", "10 000", "10 000", "10 000", "10 000"],
              ["ИИ в процедурах, год", "20 000", "20 000", "20 000", "20 000"],
              [
                "Модули на 1 компанию, всего",
                "140 000",
                "220 000",
                "340 000",
                "670 000",
              ],
            ],
          },
        ],
      },
      {
        title: "Финансовый план",
        body: "Финансовая модель в документе рассчитана на 12 месяцев и включает выручку по модулям запросов и документирования, фонд оплаты труда, партнерские выплаты, бухгалтерию и налог.",
        points: [
          "Выручка за год по модулю запросов: 13 980.",
          "Выручка за год по модулю документирования: 14 400.",
          "Совокупная выручка за год: 28 380.",
          "Расходы за год: 25 055.",
          "Прибыль до УСН: 3 325.",
          "УСН: 997.",
          "Чистая прибыль за год: 2 327.",
          "В документе не заполнены поля объема инвестиций, окупаемости и прогноза выручки в резюме; их стоит уточнить отдельно.",
        ],
        charts: [
          {
            title: "Выручка по месяцам",
            unit: "тыс. руб.",
            items: [
              { label: "Авг", value: 210 },
              { label: "Сен", value: 210 },
              { label: "Окт", value: 1680, valueLabel: "1 680" },
              { label: "Ноя", value: 1680, valueLabel: "1 680" },
              { label: "Дек", value: 2850, valueLabel: "2 850" },
              { label: "Янв", value: 2850, valueLabel: "2 850" },
              { label: "Фев", value: 4530, valueLabel: "4 530" },
              { label: "Мар", value: 4530, valueLabel: "4 530" },
              { label: "Апр", value: 4530, valueLabel: "4 530" },
              { label: "Май", value: 4530, valueLabel: "4 530" },
              { label: "Июн", value: 390 },
              { label: "Июл", value: 390 },
            ],
          },
          {
            title: "Годовая структура: выручка, расходы, прибыль",
            unit: "тыс. руб.",
            items: [
              { label: "Выручка", value: 28380, valueLabel: "28 380" },
              { label: "Расходы", value: 25055, valueLabel: "25 055" },
              { label: "Прибыль", value: 3325, valueLabel: "3 325" },
              { label: "Чистая прибыль", value: 2327, valueLabel: "2 327" },
            ],
          },
        ],
        tables: [
          {
            title: "Финансовый план на год",
            headers: ["Показатель", "1 год"],
            rows: [
              ["Выручка: модуль запросов", "13 980"],
              ["Выручка: модуль документирования", "14 400"],
              ["Выручка всего", "28 380"],
              ["Зарплата", "(8 358)"],
              ["Страховые взносы", "(2 507)"],
              ["Партнеры", "(14 190)"],
              ["Бухгалтерия", "(156)"],
              ["Расходы всего", "(25 055)"],
              ["Прибыль", "3 325"],
              ["УСН", "(997)"],
              ["Чистая прибыль", "2 327"],
            ],
          },
        ],
      },
      {
        title: "Риски проекта",
        body: "В документе выделен отдельный раздел рисков, но детальная расшифровка не заполнена. Часть рисков можно вывести из SWOT и конкурентного анализа.",
        points: [
          "Конкуренты могут добавить дашборды и клиентские кабинеты в существующие аудиторские продукты.",
          "Переход аудита к онлайн-проверкам может изменить требования к продукту и ожидания пользователей.",
          "Слабая связность процедур и рабочих документов может снизить ценность модуля документирования, если не решить повторную загрузку входящих данных.",
          "Нужны уточнения по инвестициям, окупаемости, прогнозу выручки и владельцам ключевых операционных процессов.",
        ],
      },
    ],
  },
  {
    id: "competitor-analysis",
    title: "Анализ конкурентов",
    summary:
      "Карта конкурентного поля и критерии, по которым Doka стоит сравнивать с универсальными CRM и нишевыми решениями.",
    updatedAt: "Июнь 2026",
    readTime: "5 мин",
    sections: [
      {
        title: "Группы конкурентов",
        body: "Сравнение полезнее вести не по отдельным брендам, а по типам решений и тому, какую задачу клиент пытается закрыть.",
        points: [
          "Универсальные CRM для продаж и маркетинга.",
          "Таблицы и внутренние базы, которые команды ведут вручную.",
          "Отраслевые решения для учета клиентов и проектов.",
        ],
      },
      {
        title: "Критерии сравнения",
        body: "Для Doka важны критерии, связанные с внедрением, качеством данных и ежедневной операционной работой.",
        points: [
          "Скорость запуска на существующей базе.",
          "Гибкость полей, фильтров и импортов.",
          "Стоимость владения и сложность поддержки.",
          "Наличие отраслевых сценариев для аудиторских компаний.",
        ],
      },
      {
        title: "Возможности",
        body: "Главное окно возможностей: занять позицию легкой CRM для аудиторского рынка с готовой базой знаний и методологией продаж.",
        points: [
          "Подготовить шаблоны сегментации и типовые отчеты.",
          "Собрать материалы внедрения в базе знаний.",
          "Сделать импорт и очистку данных заметным преимуществом продукта.",
        ],
      },
    ],
  },
];

export const KnowledgeBasePage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { articleId } = useParams();
  const activeArticle = articleId
    ? articles.find((article) => article.id === articleId)
    : undefined;

  const content = (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 py-4 sm:py-6">
      {activeArticle ? (
        <KnowledgeArticleView article={activeArticle} translate={translate} />
      ) : (
        <KnowledgeBaseIndex translate={translate} />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="text-lg font-semibold">
            {translate("crm.knowledge_base.title")}
          </h1>
        </MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }

  return content;
};

const KnowledgeBaseIndex = ({
  translate,
}: {
  translate: ReturnType<typeof useTranslate>;
}) => (
  <>
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <BookOpenText className="h-4 w-4" />
        {translate("crm.knowledge_base.section_label")}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">
            {translate("crm.knowledge_base.title")}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            {translate("crm.knowledge_base.description")}
          </p>
        </div>
        <Badge variant="secondary" className="w-fit">
          {articles.length} {translate("crm.knowledge_base.articles_count")}
        </Badge>
      </div>
    </div>

    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {articles.map((article) => (
        <Link
          key={article.id}
          to={getKnowledgeArticlePath(article.id)}
          className="group no-underline"
        >
          <Card className="h-full transition-colors hover:bg-muted/60">
            <CardContent className="flex h-full flex-col gap-4">
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {article.updatedAt}
                </span>
                <span>{article.readTime}</span>
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <h2 className="text-lg font-semibold leading-snug break-words [overflow-wrap:anywhere]">
                  {article.title}
                </h2>
                <p className="text-sm leading-6 text-muted-foreground break-words [overflow-wrap:anywhere]">
                  {article.summary}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {article.sections.slice(0, 3).map((section) => (
                  <Badge key={section.title} variant="outline">
                    {section.title}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                {translate("crm.knowledge_base.open_article")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  </>
);

const KnowledgeArticleView = ({
  article,
  translate,
}: {
  article: KnowledgeArticle;
  translate: ReturnType<typeof useTranslate>;
}) => (
  <article className="mx-auto grid w-full max-w-7xl gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
    <aside className="hidden lg:block">
      <div className="sticky top-16 space-y-4">
        <Button asChild variant="ghost" className="-ml-2">
          <Link to={KnowledgeBasePage.path}>
            <ArrowLeft className="h-4 w-4" />
            {translate("crm.knowledge_base.back_to_index")}
          </Link>
        </Button>
        <nav className="rounded-md border bg-card p-2">
          <div className="px-2 pb-2 text-xs font-medium uppercase text-muted-foreground">
            Разделы
          </div>
          <div className="space-y-1">
            {article.sections.map((section, index) => (
              <a
                key={section.title}
                href={`#${getSectionId(index)}`}
                className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground no-underline transition-colors hover:bg-muted hover:text-foreground"
              >
                {section.title}
              </a>
            ))}
          </div>
        </nav>
      </div>
    </aside>

    <div className="min-w-0 space-y-6">
      <Button asChild variant="ghost" className="-ml-2 lg:hidden">
        <Link to={KnowledgeBasePage.path}>
          <ArrowLeft className="h-4 w-4" />
          {translate("crm.knowledge_base.back_to_index")}
        </Link>
      </Button>

      <header className="rounded-md border bg-muted/30 px-5 py-5 sm:px-6 sm:py-6">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <Badge variant="secondary">
              {translate("crm.knowledge_base.section_label")}
            </Badge>
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {translate("crm.knowledge_base.updated")} {article.updatedAt}
            </span>
            <span>{article.readTime}</span>
          </div>
          <div>
            <h1 className="max-w-4xl text-2xl font-semibold tracking-normal sm:text-4xl">
              {article.title}
            </h1>
            <p className="mt-3 max-w-4xl text-sm leading-6 text-muted-foreground sm:text-base sm:leading-7">
              {article.summary}
            </p>
          </div>
        </div>
      </header>

      {article.metrics?.length ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {article.metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-md border bg-card px-4 py-3 shadow-xs"
            >
              <div className="space-y-1.5">
                <div className="text-[11px] font-medium uppercase leading-4 text-muted-foreground">
                  {metric.label}
                </div>
                <div className="text-2xl font-semibold leading-8 tracking-normal">
                  {metric.value}
                </div>
                <div className="text-xs leading-4 text-muted-foreground">
                  {metric.hint}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-5">
        {article.sections.map((section, index) => (
          <Card
            key={section.title}
            id={getSectionId(index)}
            className="scroll-mt-20 gap-0 py-0 shadow-xs"
          >
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
                  {index + 1}
                </div>
                <div className="min-w-0 space-y-2">
                  <h2 className="text-xl font-semibold leading-7 tracking-normal">
                    {section.title}
                  </h2>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {section.body}
                  </p>
                </div>
              </div>

              <ul className="space-y-2 rounded-md border bg-muted/20 p-4">
                {section.points.map((point) => (
                  <li
                    key={point}
                    className="flex gap-2 text-sm leading-6 text-foreground"
                  >
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                    <span className="break-words [overflow-wrap:anywhere]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>

              <KnowledgeSectionVisuals
                charts={section.charts}
                tables={section.tables}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </article>
);

const KnowledgeSectionVisuals = ({
  charts,
  tables,
}: {
  charts?: KnowledgeBarChart[];
  tables?: KnowledgeTable[];
}) => {
  if (!charts?.length && !tables?.length) return null;

  return (
    <div className="space-y-4 pt-2">
      {charts?.length ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {charts.map((chart) => (
            <KnowledgeBarChartView key={chart.title} chart={chart} />
          ))}
        </div>
      ) : null}
      {tables?.map((table) => (
        <KnowledgeTableView key={table.title} table={table} />
      ))}
    </div>
  );
};

const KnowledgeTableView = ({ table }: { table: KnowledgeTable }) => (
  <div className="overflow-hidden rounded-md border bg-background">
    <div className="space-y-1 border-b bg-muted/40 px-4 py-3">
      <h3 className="text-sm font-medium">{table.title}</h3>
      {table.caption ? (
        <p className="text-xs leading-5 text-muted-foreground">
          {table.caption}
        </p>
      ) : null}
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            {table.headers.map((header) => (
              <TableHead
                key={header}
                className="h-9 min-w-32 whitespace-normal"
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.rows.map((row, index) => (
            <TableRow
              key={`${table.title}-${index}`}
              className={index % 2 === 1 ? "bg-muted/20" : undefined}
            >
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={`${table.title}-${index}-${cellIndex}`}
                  className={
                    cellIndex === 0
                      ? "min-w-40 align-top font-medium"
                      : "min-w-32 align-top"
                  }
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const KnowledgeBarChartView = ({ chart }: { chart: KnowledgeBarChart }) => {
  const maxValue = Math.max(...chart.items.map((item) => item.value), 1);

  return (
    <div className="rounded-md border bg-background p-4 shadow-xs">
      <div className="mb-4 space-y-1">
        <h3 className="text-sm font-medium">{chart.title}</h3>
        {chart.caption ? (
          <p className="text-xs leading-5 text-muted-foreground">
            {chart.caption}
          </p>
        ) : null}
      </div>
      <div className="space-y-3">
        {chart.items.map((item) => {
          const width = `${Math.max((item.value / maxValue) * 100, 3)}%`;
          return (
            <div
              key={`${chart.title}-${item.label}`}
              className="grid gap-2 sm:grid-cols-[120px_minmax(0,1fr)_96px] sm:items-center"
            >
              <div className="text-sm font-medium">{item.label}</div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary shadow-[0_0_0_1px_hsl(var(--primary)/0.08)]"
                  style={{ width }}
                />
              </div>
              <div className="text-sm text-muted-foreground sm:text-right">
                {item.valueLabel ?? formatChartNumber(item.value)}
                {chart.unit ? ` ${chart.unit}` : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatChartNumber = (value: number) =>
  new Intl.NumberFormat("ru-RU").format(value);

const getSectionId = (index: number) => `section-${index + 1}`;

const getKnowledgeArticlePath = (articleId: string) =>
  `${KnowledgeBasePage.path}/${articleId}`;

KnowledgeBasePage.path = "/knowledge-base";
KnowledgeBasePage.detailPath = "/knowledge-base/:articleId";
